export type ErrorCategory =
  | 'DATA_FORMAT'
  | 'MISSING_RESOURCE'
  | 'SERVICE_DOWN'
  | 'PROXY_CONFIG'
  | 'TIMING_ISSUE'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

export interface CategorizedError {
  category: ErrorCategory;
  originalError: string;
  suggestion: string;
  timestamp: Date;
  context?: string;
}

export class DevErrorMonitor {
  private static internalErrorPatterns: Record<string, ErrorCategory> = {
    'SyntaxError: Unexpected token': 'DATA_FORMAT',
    "Unexpected token '<'": 'DATA_FORMAT',
    '<!DOCTYPE': 'DATA_FORMAT',
    'is not valid JSON': 'DATA_FORMAT',
    'Failed to load resource': 'MISSING_RESOURCE',
    '404 (Not Found)': 'MISSING_RESOURCE',
    ECONNREFUSED: 'SERVICE_DOWN',
    ENOTFOUND: 'SERVICE_DOWN',
    'Connection refused': 'SERVICE_DOWN',
    'Network Error': 'NETWORK_ERROR',
    'fetch is not defined': 'NETWORK_ERROR',
    proxy: 'PROXY_CONFIG',
    TimeoutError: 'TIMING_ISSUE',
    timeout: 'TIMING_ISSUE',
    'race condition': 'TIMING_ISSUE',
  };

  private static internalSuggestions: Record<ErrorCategory, string> = {
    DATA_FORMAT:
      '🔍 Check API response structure and data mapping. Verify TypeScript interfaces match API responses.',
    MISSING_RESOURCE:
      '📁 Verify file exists and URL is correct. Check if image processing has completed.',
    SERVICE_DOWN:
      '🚫 Check if backend service is running on correct port. Verify server configuration.',
    PROXY_CONFIG:
      '🔄 Verify proxy configuration in craco.config.js. Check development vs production URLs.',
    TIMING_ISSUE: '⏱️ Service initialization timing issue. Consider using service readiness gates.',
    NETWORK_ERROR: '🌐 Network connectivity issue. Check internet connection and API endpoints.',
    UNKNOWN: '❓ Review error details and check documentation for similar issues.',
  };

  private static internalErrorHistory: CategorizedError[] = [];
  private static internalIsMonitoringEnabled: boolean = false;
  private static internalOriginalConsoleError: typeof console.error;
  private static internalOriginalConsoleWarn: typeof console.warn;

  static setupMonitoring(): void {
    if (process.env.NODE_ENV !== 'development') return;
    if (this.internalIsMonitoringEnabled) return;

    this.internalIsMonitoringEnabled = true;
    this.internalOriginalConsoleError = console.error;
    this.internalOriginalConsoleWarn = console.warn;

    // Override console.error
    console.error = (...args: any[]) => {
      const errorText = this.argsToString(args);
      const categorized = this.categorizeError(errorText);

      if (categorized.category !== 'UNKNOWN') {
        console.group(`🐛 ${categorized.category} Error Detected`);
        this.internalOriginalConsoleError(...args);
        console.info(`💡 ${categorized.suggestion}`);
        console.info(`🕐 ${categorized.timestamp.toLocaleTimeString()}`);
        console.groupEnd();
      } else {
        this.internalOriginalConsoleError(...args);
      }

      this.internalErrorHistory.push(categorized);
      this.pruneHistory();
    };

    // Override console.warn for specific patterns
    console.warn = (...args: any[]) => {
      const warnText = this.argsToString(args);
      const categorized = this.categorizeError(warnText);

      if (categorized.category !== 'UNKNOWN') {
        console.group(`⚠️ ${categorized.category} Warning`);
        this.internalOriginalConsoleWarn(...args);
        console.info(`💡 ${categorized.suggestion}`);
        console.groupEnd();
      } else {
        this.internalOriginalConsoleWarn(...args);
      }
    };

    // Monitor unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorText = event.reason?.message || event.reason?.toString() || 'Unknown rejection';
      const categorized = this.categorizeError(errorText, 'Promise rejection');

      console.group(`🚨 Unhandled Promise Rejection - ${categorized.category}`);
      console.error('Unhandled promise rejection:', event.reason);
      console.info(`💡 ${categorized.suggestion}`);
      console.groupEnd();

      this.internalErrorHistory.push(categorized);
    });

    console.info('🔍 Development Error Monitor initialized');
  }

  static disableMonitoring(): void {
    if (!this.internalIsMonitoringEnabled) return;

    console.error = this.internalOriginalConsoleError;
    console.warn = this.internalOriginalConsoleWarn;
    this.internalIsMonitoringEnabled = false;

    console.info('🔍 Development Error Monitor disabled');
  }

  static categorizeError(errorText: string, context?: string): CategorizedError {
    let category: ErrorCategory = 'UNKNOWN';

    for (const [pattern, cat] of Object.entries(this.internalErrorPatterns)) {
      if (errorText.toLowerCase().includes(pattern.toLowerCase())) {
        category = cat;
        break;
      }
    }

    return {
      category,
      originalError: errorText,
      suggestion: this.internalSuggestions[category],
      timestamp: new Date(),
      context,
    };
  }

  static getErrorHistory(): CategorizedError[] {
    return [...this.internalErrorHistory];
  }

  static getErrorSummary(): Record<ErrorCategory, number> {
    const summary: Record<ErrorCategory, number> = {
      DATA_FORMAT: 0,
      MISSING_RESOURCE: 0,
      SERVICE_DOWN: 0,
      PROXY_CONFIG: 0,
      TIMING_ISSUE: 0,
      NETWORK_ERROR: 0,
      UNKNOWN: 0,
    };

    this.internalErrorHistory.forEach((error) => {
      summary[error.category]++;
    });

    return summary;
  }

  static clearHistory(): void {
    this.internalErrorHistory = [];
    console.info('🧹 Error history cleared');
  }

  static logSummary(): void {
    const summary = this.getErrorSummary();
    const totalErrors = Object.values(summary).reduce((sum, count) => sum + count, 0);

    if (totalErrors === 0) {
      console.info('✅ No categorized errors in history');
      return;
    }

    console.group(`📊 Error Summary (${totalErrors} total)`);
    Object.entries(summary).forEach(([category, count]) => {
      if (count > 0) {
        console.info(`${category}: ${count} occurrence${count > 1 ? 's' : ''}`);
      }
    });
    console.groupEnd();
  }

  private static argsToString(args: any[]): string {
    return args
      .map((arg) =>
        typeof arg === 'string' ? arg : arg?.message || JSON.stringify(arg) || String(arg)
      )
      .join(' ');
  }

  private static pruneHistory(): void {
    // Keep only last 50 errors
    if (this.internalErrorHistory.length > 50) {
      this.internalErrorHistory = this.internalErrorHistory.slice(-50);
    }
  }
}
